import React from "react";
import { ShoppingCart, TrendingUp, Zap, BarChart3, Package, Users, ChevronRight } from "lucide-react";

const DYNICTY_LOGO = "/brand/dynicty-logo-official.webp";
const DYNI_MASCOT = "/brand/dyni-mascot.webp";

const VALUES = [
  { Icon: ShoppingCart, title: "Build", sub: "Simpler Tools", tone: "cyan", glow: "rgba(0,210,255,0.2)", border: "rgba(0,210,255,0.3)", bg: "linear-gradient(135deg, rgba(6,182,212,0.2), rgba(37,99,235,0.3))", color: "#67e8f9" },
  { Icon: TrendingUp, title: "Grow", sub: "Bigger Opportunities", glow: "rgba(99,102,241,0.2)", border: "rgba(129,140,248,0.3)", bg: "linear-gradient(135deg, rgba(99,102,241,0.2), rgba(147,51,234,0.3))", color: "#a5b4fc" },
  { Icon: Zap, title: "Empower", sub: "A Stronger Future", glow: "rgba(236,72,153,0.2)", border: "rgba(232,121,249,0.3)", bg: "linear-gradient(135deg, rgba(217,70,239,0.2), rgba(219,39,119,0.3))", color: "#f0abfc" },
];

const BADGES = [
  { Icon: BarChart3, label: "Track Orders", cls: "top-2 right-6 dyn-float-delay", color: "#67e8f9", bg: "rgba(6,182,212,0.2)", border: "rgba(0,210,255,0.3)" },
  { Icon: Package, label: "Manage Products", cls: "bottom-24 -left-2 dyn-float-slow", color: "#93c5fd", bg: "rgba(59,130,246,0.2)", border: "rgba(96,165,250,0.3)" },
  { Icon: Users, label: "Grow Your Business", cls: "bottom-8 right-0 dyn-float-delay", color: "#d8b4fe", bg: "rgba(168,85,247,0.2)", border: "rgba(192,132,252,0.3)" },
];

export function DynictyBrandPanel() {
  return (
    <section className="hidden lg:flex lg:col-span-7 flex-col justify-center select-none" data-testid="dynicty-brand-panel">
      <img src={DYNICTY_LOGO} alt="Dynicty" className="h-24 w-auto object-contain object-left -ml-3 mb-6 dyn-rise" data-testid="dynicty-logo" />

      <div className="space-y-3 max-w-xl dyn-rise dyn-rise-1">
        <h1 className="text-4xl xl:text-5xl font-extrabold tracking-tight text-white leading-tight">
          More Than<br />A System<br />
          <span className="dyn-gradient-text">A Partner In Your Journey</span>
        </h1>
        <p className="text-sm xl:text-base leading-relaxed max-w-lg" style={{ color: "#cbd5e1" }}>
          We provide simpler tools, bigger opportunities, and a stronger future for your business.
        </p>
      </div>

      <div className="relative mt-8 min-h-[380px] flex items-center">
        <div className="flex flex-col gap-3.5 z-10 w-64 shrink-0 dyn-rise dyn-rise-2">
          {VALUES.map((v) => (
            <div key={v.title} className="dyn-glass-pill p-2.5 px-3.5 rounded-2xl flex items-center gap-3.5" data-testid={`dynicty-value-${v.title.toLowerCase()}`}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: v.bg, border: `1px solid ${v.border}`, boxShadow: `0 0 15px ${v.glow}`, color: v.color }}>
                <v.Icon size={20} />
              </div>
              <div>
                <p className="text-sm font-bold text-white leading-snug">{v.title}</p>
                <p className="text-xs" style={{ color: "#94a3b8" }}>{v.sub}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="relative flex-1 flex items-center justify-center dyn-rise dyn-rise-3">
          <div className="relative z-10 dyn-float-slow">
            <div className="absolute inset-0 rounded-full scale-90" style={{ background: "linear-gradient(45deg, rgba(0,210,255,0.3), rgba(99,102,241,0.2), transparent)", filter: "blur(40px)" }} />
            <img src={DYNI_MASCOT} alt="Dyni, maskot Dynicty" className="w-72 xl:w-80 h-auto object-contain relative z-10" style={{ filter: "drop-shadow(0 15px 35px rgba(0,210,255,0.4))" }} data-testid="dyni-mascot" />
            <div className="absolute -left-14 top-6 dyn-glass-card px-3.5 py-2.5 rounded-2xl rounded-tr-none text-left max-w-[150px] z-20" style={{ borderColor: "rgba(0,210,255,0.4)", boxShadow: "0 4px 20px rgba(0,210,255,0.2)" }} data-testid="dyni-speech-bubble">
              <p className="text-xs font-extrabold" style={{ color: "#67e8f9" }}>Hi! <span className="text-white">I'm Dyni</span></p>
              <p className="text-[11px] leading-tight mt-0.5" style={{ color: "#cbd5e1" }}>Always here to help you grow!</p>
            </div>
          </div>
          {BADGES.map((b) => (
            <div key={b.label} className={`absolute flex dyn-glass-pill px-3 py-1.5 rounded-xl items-center gap-2 text-xs z-20 ${b.cls}`} style={{ borderColor: b.border, boxShadow: `0 0 15px ${b.bg}` }}>
              <span className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: b.bg, color: b.color }}><b.Icon size={14} /></span>
              <p className="font-semibold text-white leading-none text-[11px]">{b.label}</p>
              <ChevronRight size={12} style={{ color: b.color, opacity: 0.8 }} />
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 pt-3 flex items-center justify-between dyn-rise dyn-rise-4" style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
        <div className="text-lg font-bold tracking-wide -rotate-2" style={{ color: "#67e8f9", textShadow: "0 0 8px rgba(0,210,255,0.4)" }}>Let's Build Together!</div>
        <div className="text-xs tracking-wider italic" style={{ color: "#94a3b8" }}>Different Businesses, Same Partner</div>
      </div>
    </section>
  );
}
